import React, { useMemo } from 'react'

import { datadogRum } from '@datadog/browser-rum'
import { Box, Heading4, Join, Space } from '@gousto-internal/citrus-react'

import type { RecipeCustomisations } from '@library/api-menu-experience'
import type { Recipe, RecipeUUID } from '@library/menu-service'
import { ExperienceIngredientOptions } from '@library/recipe-customisations'

import { useCurrentCollectionId } from 'hooks/menu-collections'
import { useMenuMeta } from 'hooks/menu-metadata/useMenuMeta'
import { useRecipes } from 'hooks/menu-recipes/useRecipes'
import { useCustomisationEducation } from 'routes/Menu/MenuRecipesPage/RecipeList/useCustomisationEducation'
import css from 'routes/Menu/components/Detail/Options/RecipeOptions.css'
import { RecipeOptionsItem } from 'routes/Menu/components/Detail/Options/RecipeOptionsItem'
import { useTrackVariantListDisplay } from 'routes/Menu/components/RecipeAlternativeOptions/RecipeAlternativeOptions/useTracking'

type RecipeOptionsProps = {
  customisations?: RecipeCustomisations
  selectedRecipeId: RecipeUUID
  onSelectRecipe: (recipeId: RecipeUUID) => void
}

const preventPropagation = (e: React.SyntheticEvent) => e.stopPropagation()

const mapRecipeCustomisationSurchargeLabel = (
  customisations: Extract<RecipeCustomisations, { type: 'recipes' }>,
  recipeId: RecipeUUID,
): string | null =>
  customisations.options.find((option) => option.recipeId === recipeId)?.surcharge
    ?.perPortionSurcharge || null

export const RecipeOptions = ({
  customisations,
  selectedRecipeId,
  onSelectRecipe,
}: RecipeOptionsProps) => {
  const { menuId } = useMenuMeta()
  const currentCollectionId = useCurrentCollectionId()
  const { closeEducation } = useCustomisationEducation()
  const hasAlternativeOptions =
    (customisations?.type === 'ingredients' &&
      customisations.slots.some((slot) => slot.values.length > 1)) ||
    (customisations?.type === 'recipes' && customisations.options.length > 1)

  useTrackVariantListDisplay({
    hasAlternativeOptions: Boolean(hasAlternativeOptions),
    view: 'details',
  })

  const ingredientOptionIds = useMemo(() => {
    if (customisations?.type !== 'ingredients') {
      return []
    }

    const slotRecipeIds = customisations.slots.flatMap((slot) =>
      slot.values
        .map((value) => value.recipeId)
        .filter((recipeId): recipeId is RecipeUUID => Boolean(recipeId)),
    )

    return Array.from(new Set([selectedRecipeId, ...slotRecipeIds]))
  }, [customisations, selectedRecipeId])

  const ingredientSlots = useMemo(
    () =>
      customisations?.type === 'ingredients'
        ? customisations.slots.map((slot) => ({
            text: slot.text,
            values: slot.values.map((value) => ({
              recipeId: value.recipeId,
              name: value.name,
              isAvailable: value.isAvailable,
            })),
          }))
        : [],
    [customisations],
  )

  const optionRecipes: Recipe[] = useRecipes(ingredientOptionIds).filter(
    (recipe): recipe is Recipe => recipe !== null,
  )

  const optionByRecipeId = useMemo(
    () => new Map(optionRecipes.map((recipe) => [recipe.id, recipe])),
    [optionRecipes],
  )

  const handleSelectRecipe = (recipeId: RecipeUUID) => {
    datadogRum.addAction('customise_recipe', {
      menu_uuid: menuId,
      optionsType: customisations?.type,
      customisations_selected: recipeId,
      source: 'recipe_details',
    })

    onSelectRecipe(recipeId)
  }

  if (!customisations || customisations.type === 'none' || !hasAlternativeOptions) {
    return null
  }

  if (customisations.type === 'ingredients') {
    return (
      <ExperienceIngredientOptions
        menuId={menuId}
        currentCollectionId={currentCollectionId}
        view="view_recipe_detail_screen"
        recipeUuid={selectedRecipeId}
        ingredientSlots={ingredientSlots}
        selectedRecipeId={selectedRecipeId}
        options={Array.from(optionByRecipeId.values()).map((recipe) => ({
          recipe: { id: recipe.id },
          isOutOfStock:
            !recipe.isAvailable ||
            customisations.slots.some((slot) =>
              slot.values.some((value) => value.recipeId === recipe.id && !value.isAvailable),
            ),
        }))}
        onSelectRecipe={handleSelectRecipe}
        closeCustomisationEducation={closeEducation}
      />
    )
  }

  return (
    <Box
      className={css.recipeList}
      role="button"
      tabIndex={-1}
      onClick={preventPropagation}
      onKeyDown={preventPropagation}
      paddingBottom={4}
    >
      <Join with={<Space size={4} />}>
        <Heading4>Choose an option</Heading4>
        <ul className={css.recipeListText}>
          {customisations.options.map((option) => (
            <RecipeOptionsItem
              key={option.recipeId}
              recipeId={option.recipeId}
              recipeName={option.name}
              changeCheckedRecipe={() => handleSelectRecipe(option.recipeId)}
              isChecked={selectedRecipeId === option.recipeId}
              isOutOfStock={!option.isAvailable}
              surchargeLabel={mapRecipeCustomisationSurchargeLabel(customisations, option.recipeId)}
            />
          ))}
        </ul>
      </Join>
    </Box>
  )
}
